<template>
  <b-modal size="lg" class="modal-box" ref="brandItemSettingsModal"
    :lazy="false"
    :visible="false"
    @shown="getBrandSettings"
    @ok.prevent="saveSettings"
    @cancel="settings_fields = []"
    ok-title="Save"
    ok-variant="primary"
    cancel-variant="default"
    title="Brand Settings"
    title-class="w-100 text-md-center">

    <div class="alert alert-warning small mb-3">
      Department settings take priority over brand settings. If a product's department, class, or fineline has a setting configured, it will override what's set here.
    </div>

    <div v-if="loading" class="d-flex justify-content-center">
      <b-spinner style="width: 3rem; height: 3rem;" class="text-center"></b-spinner>
    </div>
    <div v-else-if="settings_fields.length == 0">
      <h3 class="text-center">There aren't any settings fields.</h3>
    </div>
    <div v-else-if="saving">
      <div class="col text-center">
        <h3>Saving...</h3>
        <b-spinner style="width: 3rem; height: 3rem;" class="text-center"></b-spinner>
      </div>
    </div>
    <div v-cloak v-else>
      <div v-for="field in settings_fields" :key="field.key" class="mb-4">
        <div class="d-flex align-items-center">
          <b-form-checkbox v-if="field.type == 'bool'" v-model="settings_values[field.key]" switch class="mt-0" />
          <h6 class="my-0">{{ field.editorName }}</h6>
        </div>
        <p v-if="field.description" class="mt-1 mb-2 text-muted">{{ field.description }}</p>
        <b-input v-if="field.type == 'int'" v-model="settings_values[field.key]" :placeholder="field.editorName" type="number" class="mb-3"></b-input>
        <b-textarea
          v-if="field.type == 'text' && field.key != 'metaTitle' && field.key != 'metaDescription' && field.key != 'topDescription' && field.key != 'bottomDescription'"
          v-model="settings_values[field.key]"
          :placeholder="field.editorName"
          :class="[validateTextarea(field.key)]"
        ></b-textarea>
        <template v-if="field.key == 'metaTitle'">
          <b-input
            v-model="settings_values[field.key]"
            :placeholder="field.editorName"
            maxlength="60"
            :class="[validateTextarea(field.key)]"
          ></b-input>
          <small class="text-muted">{{ (settings_values[field.key] || '').length }}/60 characters</small>
        </template>
        <template v-else-if="field.key == 'metaDescription'">
          <b-textarea
            v-model="settings_values[field.key]"
            :placeholder="field.editorName"
            maxlength="160"
            :class="[validateTextarea(field.key)]"
          ></b-textarea>
          <small class="text-muted">{{ (settings_values[field.key] || '').length }}/160 characters</small>
        </template>
        <template v-else-if="field.key == 'topDescription'">
          <HtmlEditor v-model="settings_values[field.key]" :config="textOnlyEditorConfig" />
          <small class="text-muted">{{ (settings_values[field.key] || '').length }}/700 characters</small>
        </template>
        <template v-else-if="field.key == 'bottomDescription'">
          <HtmlEditor v-model="settings_values[field.key]" :config="textOnlyEditorConfig" />
          <small class="text-muted">{{ (settings_values[field.key] || '').length }}/5000 characters</small>
        </template>
      </div>
    </div>
  </b-modal>
</template>

<script>
import AdminService from '@/api-services/admin.service';

export default {
  name: 'BrandItemSettings',
  props: {
    currentBrand: {
      type: Object,
      default: null
    },
    selectedStore: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      settings_fields: [],
      settings_values: {},
      saving: false,
      loading: false,
      textOnlyEditorConfig: {
        menus: [
          'bold', 'underline', 'italic', 'strikethrough',
          'forecolor', 'bgcolor', '|',
          'quote', 'fontsize', 'head',
          'unorderlist', 'orderlist',
          'alignleft', 'aligncenter', 'alignright', '|',
          'link', 'unlink', '|',
          'undo', 'fullscreen'
        ]
      },
      limit_text_fields: [
        'qtyMessage',
        'hidePriceText',
        'disableShippingText',
        'disableOrderingText',
        'disableDeliveryText',
        'metaTitle',
        'metaDescription',
        'topDescription',
        'bottomDescription',
      ],
    };
  },
  methods: {
    validateTextarea(key) {
      const limits = { metaTitle: 60, metaDescription: 160, topDescription: 700, bottomDescription: 5000 };
      const limit = limits[key] || 50;
      if (this.settings_values[key] && this.limit_text_fields.includes(key)) {
        return this.settings_values[key].length > limit ? 'is-invalid'
          : this.settings_values[key].length > 0 ? 'is-valid'
          : '';
      }
      return '';
    },
    async getBrandSettings() {
      this.loading = true;
      this.settings_fields = [];
      try {
        const response = await AdminService.getBrandItemSettings(this.currentBrand.brand_id, this.selectedStore);
        const fields = response.data.fields;
        const values = response.data.values;
        this.settings_fields = fields;
        if (values && values[0] != null) {
          const parsed = JSON.parse(values[0]);
          this.createSettingsValues(fields, parsed);
        } else {
          fields.forEach(field => {
            this.$set(this.settings_values, field.key, undefined);
          });
        }
      } catch {
        this.$swal('Failed to load settings. Please try again.', '', 'error');
      } finally {
        this.loading = false;
      }
    },
    showModal() {
      this.settings_values = {};
      this.$refs.brandItemSettingsModal.show();
    },
    createSettingsValues(fields, values) {
      fields.forEach(field => {
        this.$set(this.settings_values, field.key, values[field.key]);
      });
    },
    cleanValues(obj) {
      for (var prop in obj) {
        if (obj[prop] === null || obj[prop] === undefined || obj[prop] === '') {
          delete obj[prop];
        }
      }
      return obj;
    },
    async saveSettings() {
      const validation = [];
      const fieldLimits = { metaTitle: 60, metaDescription: 160, topDescription: 700, bottomDescription: 5000 };
      this.limit_text_fields.forEach(key => {
        const limit = fieldLimits[key] || 50;
        if (this.settings_values[key] && this.settings_values[key].length > limit) {
          const fieldLabel = (this.settings_fields.find(x => x.key == key) || {}).editorName || key;
          validation.push(`<b>${fieldLabel}:</b> Character Length should be less than ${limit}`);
        }
      });
      if (validation.length > 0) {
        this.$swal('Validation', validation.join('<br>'), 'error');
        return;
      }
      this.saving = true;
      this.$parent.saving = true;
      try {
        const cleaned = this.cleanValues({ ...this.settings_values });
        ['metaTitle', 'metaDescription', 'topDescription', 'bottomDescription'].forEach(key => {
          if (this.settings_fields.find(f => f.key === key)) {
            cleaned[key] = this.settings_values[key] || '';
          }
        });
        const settings = JSON.stringify(cleaned);
        await AdminService.saveBrandItemSettings(this.currentBrand.brand_id, settings, this.selectedStore);
        this.settings_fields = [];
        this.$refs.brandItemSettingsModal.hide();
        this.$emit('saved', { ...this.currentBrand, settings: cleaned });
      } catch {
        this.$swal('Failed to save settings. Please try again.', '', 'error');
      } finally {
        this.saving = false;
        this.$parent.saving = false;
      }
    },
  },
};
</script>
